import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, TrendingUp, AlertCircle, CheckCircle, Save, Download } from 'lucide-react';

interface BudgetCategory {
  id: string;
  name: string;
  amount: number;
  percentage: number;
  isEssential: boolean;
  description: string;
  tips: string[];
}

interface BudgetResult {
  totalIncome: number;
  totalExpenses: number;
  remaining: number;
  isHealthy: boolean;
  recommendations: string[];
  emergencyFund: number;
  savingsRate: number;
}

const InteractiveBudgetCalculator: React.FC = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([
    {
      id: 'housing',
      name: 'Housing (Rent/Mortgage)',
      amount: 0,
      percentage: 30,
      isEssential: true,
      description: 'Your monthly housing costs',
      tips: ['Try to keep this under 30% of income', 'Consider roommates to reduce costs', 'Look for rent-controlled apartments']
    },
    {
      id: 'utilities',
      name: 'Utilities',
      amount: 0,
      percentage: 5,
      isEssential: true,
      description: 'Electricity, gas, water, internet',
      tips: ['Compare utility providers', 'Use energy-efficient appliances', 'Consider budget billing plans']
    },
    {
      id: 'groceries',
      name: 'Groceries & Food',
      amount: 0,
      percentage: 15,
      isEssential: true,
      description: 'Food and household essentials',
      tips: ['Plan meals weekly', 'Buy generic brands', 'Use coupons and sales', 'Cook at home more often']
    },
    {
      id: 'transportation',
      name: 'Transportation',
      amount: 0,
      percentage: 10,
      isEssential: true,
      description: 'Car payment, gas, public transit',
      tips: ['Consider public transportation', 'Carpool when possible', 'Maintain your vehicle regularly']
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      amount: 0,
      percentage: 8,
      isEssential: true,
      description: 'Insurance, medications, doctor visits',
      tips: ['Shop for health insurance', 'Use generic medications', 'Preventive care saves money']
    },
    {
      id: 'debt',
      name: 'Debt Payments',
      amount: 0,
      percentage: 10,
      isEssential: true,
      description: 'Credit cards, loans, other debts',
      tips: ['Pay more than minimum when possible', 'Focus on high-interest debt first', 'Consider debt consolidation']
    },
    {
      id: 'savings',
      name: 'Savings & Emergency Fund',
      amount: 0,
      percentage: 20,
      isEssential: false,
      description: 'Building your financial safety net',
      tips: ['Aim for 3-6 months of expenses', 'Start with $1,000 emergency fund', 'Automate your savings']
    },
    {
      id: 'entertainment',
      name: 'Entertainment & Recreation',
      amount: 0,
      percentage: 5,
      isEssential: false,
      description: 'Movies, dining out, hobbies',
      tips: ['Look for free activities', 'Set a monthly entertainment budget', 'Cook at home for special occasions']
    },
    {
      id: 'other',
      name: 'Other Expenses',
      amount: 0,
      percentage: 7,
      isEssential: false,
      description: 'Miscellaneous expenses',
      tips: ['Track all small purchases', 'Review monthly for patterns', 'Cut unnecessary subscriptions']
    }
  ]);

  const [budgetResult, setBudgetResult] = useState<BudgetResult | null>(null);
  const [showTips, setShowTips] = useState<string | null>(null);

  useEffect(() => {
    calculateBudget();
  }, [monthlyIncome, budgetCategories]);

  const calculateBudget = () => {
    if (monthlyIncome <= 0) {
      setBudgetResult(null);
      return;
    }

    const totalExpenses = budgetCategories.reduce((sum, category) => sum + category.amount, 0);
    const remaining = monthlyIncome - totalExpenses;
    const emergencyFund = monthlyIncome * 0.1; // 10% for emergency fund
    const savingsRate = (budgetCategories.find(c => c.id === 'savings')?.amount || 0) / monthlyIncome * 100;

    const recommendations = [];
    
    if (remaining < 0) {
      recommendations.push('Your expenses exceed your income. Look for ways to reduce costs or increase income.');
    } else if (remaining < monthlyIncome * 0.1) {
      recommendations.push('Consider increasing your emergency fund or savings.');
    }

    if (savingsRate < 10) {
      recommendations.push('Try to save at least 10% of your income for emergencies and future goals.');
    }

    const housingCategory = budgetCategories.find(c => c.id === 'housing');
    if (housingCategory && housingCategory.amount > monthlyIncome * 0.3) {
      recommendations.push('Housing costs are high. Consider finding more affordable housing or getting a roommate.');
    }

    const isHealthy = remaining >= 0 && savingsRate >= 5;

    setBudgetResult({
      totalIncome: monthlyIncome,
      totalExpenses,
      remaining,
      isHealthy,
      recommendations,
      emergencyFund,
      savingsRate
    });
  };

  const updateCategoryAmount = (categoryId: string, amount: number) => {
    setBudgetCategories(prev => 
      prev.map(category => 
        category.id === categoryId 
          ? { ...category, amount }
          : category
      )
    );
  };

  const updateCategoryPercentage = (categoryId: string, percentage: number) => {
    const amount = (monthlyIncome * percentage) / 100;
    updateCategoryAmount(categoryId, amount);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const exportBudget = () => {
    const budgetData = {
      monthlyIncome,
      categories: budgetCategories,
      result: budgetResult,
      generatedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(budgetData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-budget.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Interactive Budget Calculator</h2>
        <p className="text-gray-600">
          Create a personalized budget that works for your new life in America
        </p>
      </div>

      {/* Income Input */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Income</h3>
        <div className="flex items-center gap-4">
          <DollarSign className="w-6 h-6 text-gray-500" />
          <input
            type="number"
            value={monthlyIncome || ''}
            onChange={(e) => setMonthlyIncome(Number(e.target.value))}
            placeholder="Enter your monthly income"
            className="flex-1 text-2xl font-semibold border-0 focus:ring-0 focus:outline-none"
          />
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Include all sources: salary, freelance work, benefits, etc.
        </p>
      </div>

      {/* Budget Categories */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Budget Categories</h3>
        {budgetCategories.map((category) => (
          <div key={category.id} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h4 className="font-medium text-gray-800">{category.name}</h4>
                {category.isEssential && (
                  <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                    Essential
                  </span>
                )}
                <button
                  onClick={() => setShowTips(showTips === category.id ? null : category.id)}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Tips
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Recommended: {category.percentage}%</span>
                <button
                  onClick={() => updateCategoryPercentage(category.id, category.percentage)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Apply
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="number"
                  value={category.amount || ''}
                  onChange={(e) => updateCategoryAmount(category.id, Number(e.target.value))}
                  placeholder="0"
                  className="w-full text-lg font-semibold border-0 focus:ring-0 focus:outline-none"
                />
                <div className="text-sm text-gray-600">
                  {formatCurrency(category.amount)} / month
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  {monthlyIncome > 0 ? Math.round((category.amount / monthlyIncome) * 100) : 0}% of income
                </div>
              </div>
            </div>

            {showTips === category.id && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-800 mb-2">Tips for {category.name}:</h5>
                <ul className="space-y-1">
                  {category.tips.map((tip, index) => (
                    <li key={index} className="text-sm text-blue-700 flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Budget Results */}
      {budgetResult && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Budget Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {formatCurrency(budgetResult.totalIncome)}
              </div>
              <div className="text-sm text-gray-600">Monthly Income</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {formatCurrency(budgetResult.totalExpenses)}
              </div>
              <div className="text-sm text-gray-600">Monthly Expenses</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${
                budgetResult.remaining >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(budgetResult.remaining)}
              </div>
              <div className="text-sm text-gray-600">Remaining</div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            {budgetResult.isHealthy ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
            <span className={`font-medium ${
              budgetResult.isHealthy ? 'text-green-800' : 'text-red-800'
            }`}>
              {budgetResult.isHealthy ? 'Healthy Budget!' : 'Needs Adjustment'}
            </span>
          </div>

          {budgetResult.recommendations.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">Recommendations:</h4>
              <ul className="space-y-1">
                {budgetResult.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={exportBudget}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Budget
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <Save className="w-4 h-4" />
              Print Budget
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveBudgetCalculator;
